import React, { useState, useEffect } from "react";
import Chart from "chart.js/auto";
import ApexCharts from "react-apexcharts";
import Masonry from "masonry-layout";
import DateRangePicker from "react-bootstrap-daterangepicker";
import Moment from "moment";
import { Card, CardHeader, CardBody } from "./../../components/card/card.jsx";
import "bootstrap-daterangepicker/daterangepicker.css";
import { getChartJsDefaultOptions } from "./../../composables/chartOptions.js";
import Swal from "sweetalert2";
import { Circles } from "react-loader-spinner";
import "moment/locale/es";

function Analytics() {
  const [mailData, setMailData] = useState([]);
  const [loading, setLoading] = useState(false); // Estado para el loader
  const [filters, setFilters] = useState({
    asesor: "",
    financiera: "",
    motivo_rechazo: "",
    agencia: "",
    condicion: "",
  });
  const [filterOptions, setFilterOptions] = useState({
    asesor: [],
    financiera: [],
    motivo_rechazo: [],
    agencia: [],
    condicion: [],
    startDate: "",
    endDate: "",
  });

  const accessToken =
    "1MhYphhrGUbgbdRy7cyYFGQlXS7tAi6ooUoptkHQbOm9GcsKiZM7uMF1MERxwMWq30I7Y3"; // El token de acceso
  const apiUrl = "https://api.checkmark.mx/solicitudes/consultar"; // La URL de la API

  const fetchData = async () => {
    setLoading(true); // Activar el loader

    try {
      const agenciasStorage = JSON.parse(localStorage.getItem("agencias")); // Recuperar agencias del localStorage
      const modulos = JSON.parse(localStorage.getItem("modulos")); // Recuperar los módulos del localStorage
      const userId = localStorage.getItem("userId"); // Obtener el userId desde el localStorage

      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          modulos, // Envía los módulos
          usuario_id: userId, // Envía el user_id
          agencias: agenciasStorage, // Envía las agencias
          // Otros filtros
          ...filters,
        }),
      });

      const data = await response.json();
      console.log("Respuesta de la API:", data); // Log para depuración

      if (data.statusCode === 200) {
        // Verifica si hay resultados y asigna correctamente a mailData
        if (
          Array.isArray(data.data.resultados) &&
          data.data.resultados.length > 0
        ) {
          // Procesar los datos
          const processedData = data.data.resultados.map((cliente) => {
            let productos = [];

            return {
              ...cliente,
              productos, // Añadir los productos procesados al cliente
            };
          });

          setMailData(processedData);
        } else {
          console.error("No hay registros disponibles.");
          // No hay resultados, se asigna un arreglo vacío
          setMailData([]);
          Swal.fire({
            icon: "warning",
            title: "Sin resultados",
            text: "No se encontraron resultados para los filtros aplicados.",
          });
        }
      } else if (data.statusCode === 404) {
        // No hay resultados, se asigna un arreglo vacío
        setMailData([]);
        // Mostrar alerta si el statusCode es 404
        Swal.fire({
          icon: "warning",
          title: "Sin resultados",
          text: "No se encontraron resultados para los filtros aplicados.",
        });
      } else {
        // No hay resultados, se asigna un arreglo vacío
        setMailData([]);
        console.error(
          "Error al obtener los datos:",
          data.message || "Sin mensaje de error."
        );
      }
    } catch (error) {
      // No hay resultados, se asigna un arreglo vacío
      setMailData([]);
      console.error("Error en la solicitud:", error);
    } finally {
      setLoading(false); // Desactivar el loader una vez que se termine la solicitud
    }
  };

  // Función para obtener las opciones de filtro desde la API
  const fetchFilterOptions = async () => {
    try {
      const agenciasStorage = JSON.parse(localStorage.getItem("agencias")); // Recuperar agencias del localStorage
      const modulos = JSON.parse(localStorage.getItem("modulos")); // Recuperar los módulos del localStorage
      const userId = localStorage.getItem("userId"); // Obtener el userId desde el localStorage
      const response = await fetch(apiUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          access_token: accessToken,
          modulos, // Envía los módulos
          usuario_id: userId, // Envía el user_id
          agencias: agenciasStorage, // Envía las agencias
        }),
      });

      const data = await response.json();

      if (data.statusCode === 200) {
        const { opciones } = data.data;
        setFilterOptions(opciones);
      } else {
        console.error("No se encontraron opciones para los filtros.");
      }
    } catch (error) {
      console.error("Error al obtener las opciones de filtro:", error);
    }
  };

  // Llama a `fetchData` y `fetchFilterOptions` cuando se monte el componente
  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, [filters]); //

  var chart1 = "";
  var chart2 = "";
  var chart3 = "";

  var dateRange = {
    currentWeek:
      Moment().subtract(7, "days").format("D MMM YYYY") +
      " - " +
      Moment().format("D MMM YYYY"),
    prevWeek:
      Moment().subtract(15, "days").format("D MMM") +
      " - " +
      Moment().subtract(8, "days").format("D MMM YYYY"),
  };

  const handleDateApplyEvent = (event, picker) => {
    setFilters((prevFilters) => ({
      ...prevFilters,
      startDate: picker.startDate.format("YYYY-MM-DD"),
      endDate: picker.endDate.format("YYYY-MM-DD"),
    }));
  };

  function renderChart() {
    function newDate(days) {
      return Moment().add(days, "d").format("D MMM");
    }

    // color & font variable
    var gray300Color = getComputedStyle(document.body)
      .getPropertyValue("--bs-gray-300")
      .trim();
    var gray300RgbColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-gray-300-rgb")
      .trim();
    var bodyBg = getComputedStyle(document.body)
      .getPropertyValue("--bs-body-bg")
      .trim();
    var themeColor = getComputedStyle(document.body)
      .getPropertyValue("--bs-theme")
      .trim();
    var whiteRgb = getComputedStyle(document.body)
      .getPropertyValue("--bs-white-rgb")
      .trim();

    // chart 1 - Individual field sum
    const labelsChart1 = [
      "Autorizado",
      "Rechazado",
      "Condicionado",
      "Formalizado",
      "Facturado",
    ];

    const totalItems = mailData.length; // Total de elementos
    const isDarkMode = document.body.classList.contains("theme-default");

    const backgroundColors = isDarkMode
      ? ["#4CAF50", "#F44336", "#FF9800", "#2196F3", "#9C27B0"] // colores para tema oscuro
      : ["#81C784", "#E57373", "#FFB74D", "#64B5F6", "#BA68C8"]; // colores para tema claro
      
    /*const backgroundColors = isDarkMode ? "#00e58e" : "#00366f";*/

    const dataChart1 = [
      mailData.reduce((sum, item) => sum + (item.status === "Autorizado" ? 1 : 0), 0),
      mailData.reduce((sum, item) => sum + (item.status === "Rechazado" ? 1 : 0), 0),
      mailData.reduce((sum, item) => sum + (item.status === "Condicionado" ? 1 : 0), 0),
      mailData.reduce((sum, item) => sum + (item.sub_status === "Formalizado" ? 1 : 0), 0),
      mailData.reduce((sum, item) => sum + (item.sub_status === "Facturado" ? 1 : 0), 0),
    ];
    
    const financials = [...new Set(mailData.map((entry) => entry.financiera))]; // Extrae las financieras únicas

    // Paso 2: Contar las solicitudes por financiera
    const dataChart3 = financials.map(
      (bank) => mailData.filter((entry) => entry.financiera === bank).length
    );

    // chart 1
    var chart1Container = document.getElementById("chart-1");
    if (chart1) {
      chart1.destroy();
    }
    if (chart1Container) {
      chart1Container.innerHTML =
        '<canvas id="chart1" className="w-100" height="190"></canvas>';

      const ctx = document.getElementById("chart1").getContext("2d");

      chart1 = new Chart(ctx, {
        type: "bar",
        data: {
          labels: labelsChart1,
          datasets: [
            {
              label: "Total",
              data: dataChart1,
              backgroundColor: backgroundColors,
              //borderColor: themeColor,
              borderWidth: 1.5,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: isDarkMode ? "#ffffff" : "#000000", // texto del eje X
              },
              grid: {
                color: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", // líneas
              },
            },
            y: {
              ticks: {
                color: isDarkMode ? "#ffffff" : "#000000", // texto del eje Y
              },
              grid: {
                color: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)", // líneas
              },
            },
          },
          plugins: {
            legend: {
              labels: {
                color: isDarkMode ? "#ffffff" : "#000000", // leyenda
              },
            },
            tooltip: {
              backgroundColor: isDarkMode ? "#222222" : "#ffffff",
              titleColor: isDarkMode ? "#ffffff" : "#000000",
              bodyColor: isDarkMode ? "#eeeeee" : "#000000",
            },
          },
        },
      });
    }

    // chart 2 (example with dummy dynamic data for time-series)
    var chart2Container = document.getElementById("chart-2");
    if (chart2) {
      chart2.destroy();
    }

    if (chart2Container) {
      chart2Container.innerHTML =
        '<canvas id="chart2" className="w-100" height="190"></canvas>';

      const ctx2 = document.getElementById("chart2").getContext("2d");

      chart2 = new Chart(ctx2, {
        type: "line",
        data: {
          labels: ["Formalizado", "Rechazado"],
          datasets: [
            {
              label: "Total",
              backgroundColor: "transparent",
              borderColor: isDarkMode ? "#00e58e" : "#00366f",
              borderWidth: 2,
              pointBackgroundColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverBackgroundColor: "#fff",
              pointHoverBorderColor: isDarkMode ? "#00e58e" : "#00366f",
              pointHoverRadius: 6,
              pointHoverBorderWidth: 2,
              data: [
                mailData.reduce((acc, curr) => acc + (curr.sub_status === "Formalizado" ? 1 : 0), 0),
                mailData.reduce((acc, curr) => acc + (curr.sub_status === "Rechazado" ? 1 : 0), 0),
              ],
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          scales: {
            x: {
              ticks: {
                color: isDarkMode ? "#ffffff" : "#333333",
              },
              grid: {
                color: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              },
            },
            y: {
              ticks: {
                color: isDarkMode ? "#ffffff" : "#333333",
              },
              grid: {
                color: isDarkMode ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)",
              },
            },
          },
          plugins: {
            legend: {
              labels: {
                color: isDarkMode ? "#ffffff" : "#333333",
              },
            },
            tooltip: {
              backgroundColor: isDarkMode ? "#222" : "#fff",
              titleColor: isDarkMode ? "#fff" : "#000",
              bodyColor: isDarkMode ? "#eee" : "#000",
            },
          },
        },
      });
    }

    // chart 3
    var chart3Container = document.getElementById("chart-3");
    if (chart3) {
      chart3.destroy();
    }
    if (chart3Container) {
      chart3Container.innerHTML =
        '<canvas id="chart3" className="w-100" height="190"></canvas>';

      const ctx = document.getElementById("chart3").getContext("2d");

      chart3 = new Chart(ctx, {
        type: "pie",
        data: {
          labels: financials, // Labels para cada segmento
          datasets: [
            {
              data: dataChart3, // Valores de cada segmento
              backgroundColor: [
                "rgba(" + themeColor + ", .5)",
                "rgba(" + whiteRgb + ", .2)",
                "rgba(" + gray300RgbColor + ", .5)",
              ],
              hoverBackgroundColor: [
                "rgba(" + themeColor + ", 1)",
                "rgba(" + whiteRgb + ", 1)",
                "rgba(" + gray300RgbColor + ", 1)",
              ],
              borderWidth: 0,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          plugins: {
            tooltip: {
              enabled: true, // Mantén los tooltips habilitados si los necesitas
            },
          },
        },
        plugins: [
          {
            id: "dataLabelsPlugin",
            afterDatasetsDraw(chart) {
              const { ctx, data } = chart;
              const meta = chart.getDatasetMeta(0); // Primer dataset (el único en este caso)
              const dataset = data.datasets[0].data;

              // Obtener los índices de los tres segmentos con mayores valores
              const topIndexes = dataset
                .map((value, index) => ({ value, index }))
                .sort((a, b) => b.value - a.value)
                .slice(0, 3)
                .map((item) => item.index);

              meta.data.forEach((element, index) => {
                if (topIndexes.includes(index)) {
                  // Solo dibujar si el índice está en los tres mayores
                  const label = data.labels[index];
                  const position = element.tooltipPosition();

                  // Configura el estilo del texto
                  ctx.font = "12px Arial";
                  ctx.fillStyle = "#000"; // Color blanco para el texto
                  ctx.textAlign = "center";
                  ctx.textBaseline = "middle";

                  // Añadir contorno negro al texto para mejor visibilidad
                  ctx.lineWidth = 2; // Grosor del contorno
                  ctx.strokeStyle = "#fff"; // Color del contorno (negro)
                  ctx.strokeText(label, position.x, position.y); // Dibuja el contorno
                  ctx.fillText(label, position.x, position.y); // Dibuja el texto sobre el contorno
                }
              });
            },
          },
        ],
      });
    }
  }

  // Estado para almacenar las opciones y series del gráfico
  const [options, setOptions] = useState({});
  const [funnelData, setFunnelData] = useState({
    series: [],
    categories: [],
    rawValues: [],
  });

  // Función para calcular los datos del embudo
    const calculateFunnelData = () => {
    const totalSolicitudes = mailData.length;

    // Contar por sub_status
    const formalizados = mailData.filter(item => item.sub_status === "Formalizado").length;
    const noFormalizados = mailData.filter(item => item.sub_status === "Aprobado sin formalizar").length;

    // Porcentajes respecto al total
    const pctFormalizados = totalSolicitudes > 0 ? ((formalizados / totalSolicitudes) * 100).toFixed(1) : 0;
    const pctNoFormalizados = totalSolicitudes > 0 ? ((noFormalizados / totalSolicitudes) * 100).toFixed(1) : 0;
    const pctTotal = totalSolicitudes > 0 ? ((totalSolicitudes / totalSolicitudes) * 100).toFixed(1) : 0;
    
    const funnelShape = [100, 75, 50];
    return {
      series: [
        {
          name: "Solicitudes",
          data: funnelShape, 
        },
      ],
      categories: [
        `Total Solicitudes (${pctTotal}%)`,
        `Formalizadas (${pctFormalizados}%)`,
        `Autorizadas No Formalizadas (${pctNoFormalizados}%)`,
      ],
      rawValues: [totalSolicitudes, formalizados, noFormalizados],
    };
  };

  // Función para renderizar el gráfico
  const renderChartApex = () => {
    const funnelData = calculateFunnelData(); // Calcula los datos del embudo

    const isDarkMode = document.body.classList.contains("theme-default"); // Detecta modo oscuro
    const themeColor =
      getComputedStyle(document.body).getPropertyValue("--bs-theme").trim() ||
      "#000000";

    const chartOptions = {
      chart: {
        type: "bar",
        height: 350,
        dropShadow: {
          enabled: true,
        },
      },
      plotOptions: {
        bar: {
          borderRadius: 0,
          horizontal: true,
          barHeight: "80%",
          isFunnel: true,
        },
      },
      colors: ["#4091e2", "#28A745", "#FFC107"],
      dataLabels: {
        enabled: true,
        formatter: function (val, opt) {
          const category = funnelData.categories[opt.dataPointIndex];
          const value = funnelData.rawValues[opt.dataPointIndex];
          return `${category}: ${value}`;
        },
        dropShadow: {
          enabled: true,
        },
        style: {
          fontSize: "14px",
          fontWeight: "bold",
          colors: [isDarkMode ? "#000000" : "#ffffff"], // Color de texto
        },
      },
      title: {
        text: "Solicitudes de Crédito Automotriz",
        align: "middle",
        style: {
          color: isDarkMode ? "#ffffff" : "#000000", // Color del título
        },
      },
      xaxis: {
        categories: funnelData.categories,
        labels: {
          style: {
            colors: funnelData.categories.map(() =>
              isDarkMode ? "#ffffff" : "#000000"
            ),
          },
        },
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        show: false,
      },
    };

    setOptions(chartOptions);
    setFunnelData(funnelData);
  };

  // Call the renderChart function inside useEffect
  useEffect(() => {
    getChartJsDefaultOptions();
    renderChart();
    renderChartApex();
    document.addEventListener("theme-reload", () => {
      renderChart();
      renderChartApex();
    });
  }, [mailData]);

  useEffect(() => {
    const observer = new MutationObserver(() => {
      const cancelButton = document.querySelector(".cancelBtn");
      const applyButton = document.querySelector(".applyBtn");

      // Solo cambiar el texto si no es el valor correcto
      if (cancelButton && cancelButton.textContent !== "Cancelar") {
        cancelButton.textContent = "Cancelar";
      }
      if (applyButton && applyButton.textContent !== "Aplicar") {
        applyButton.textContent = "Aplicar";
      }
    });

    const targetNode = document.body;
    observer.observe(targetNode, { childList: true, subtree: true });

    return () => observer.disconnect(); // Limpiar observador al desmontar
  }, []);

  return (
    <div>
      {loading ? (
        <div className="loader-data">
          <Circles height="80" width="80" color="#4fa94d" />
        </div>
      ) : (
        <div>
          <h1 className="page-header">
            Gráficas{" "}
            <small>estadísticas, descripción general y rendimiento</small>
          </h1>

          <div className="d-flex align-items-center mb-3">
            <DateRangePicker
              startDate={filters.startDate}
              endDate={filters.endDate}
              onApply={handleDateApplyEvent}
            >
              <a
                href="#/"
                className="btn btn-secondary btn-sm d-inline-flex align-items-center"
              >
                <span>
                  {filters.startDate && filters.endDate
                    ? `${filters.startDate} - ${filters.endDate}`
                    : "Selecciona un rango"}
                </span>
                <i className="fa fa-chevron-down ms-2 fs-8px"></i>
              </a>
            </DateRangePicker>
          </div>

          <div className="row g-2 gy-2 mb-2">
            {[
              { title: "Total", value: mailData.length }, // Total de registros
               {
                                        title: "Autorizado",
                                        value: mailData.reduce((acc, curr) => {
                                          return acc + (curr.status === "Autorizado" ? 1 : 0);
                                        }, 0),
                                      },
                                      {
                                        title: "Rechazado",
                                        value: mailData.reduce((acc, curr) => {
                                          return acc + (curr.status === "Rechazado" ? 1 : 0);
                                        }, 0),
                                      },
                                      {
                                        title: "Condicionado",
                                        value: mailData.reduce((acc, curr) => {
                                          return acc + (curr.status === "Condicionado" ? 1 : 0);
                                        }, 0),
                                      },
                                      {
                                        title: "Formalizado",
                                        value: mailData.reduce((acc, curr) => {
                                          return acc + (curr.sub_status === "Formalizado" ? 1 : 0);
                                        }, 0),
                                      },
                                      {
                                        title: "Facturado",
                                        value: mailData.reduce((acc, curr) => {
                                          return acc + (curr.sub_status === "Facturado" ? 1 : 0);
                                        }, 0),
                                      },
            ].map((item, index) => (
              <div key={index} className="col-xl-2 col-lg-2 col-6">
                <Card className="h-100">
                  <CardHeader>{item.title}</CardHeader>
                  <CardBody>
                    <div className="h4 mb-1 black-text">{item.value}</div>
                  </CardBody>
                </Card>
              </div>
            ))}

            <div className="col-sm-12 col-xl-12">
              <Card>
                <CardHeader className="d-flex">TOTAL SOLICITUDES </CardHeader>
                <CardBody>
                  <div className="d-flex align-items-center h3 mb-0">
                    <div className="black-text">{mailData.length}</div>
                  </div>

                  <hr className="black-text" />
                  <div>
                    <div className="fs-10px fw-semibold mb-3 black-text">
                      SOLICITUDES A LO LARGO DEL TIEMPO
                    </div>
                    <div className="chart mb-2" style={{ height: "190px" }}>
                      <div id="chart-1"></div>
                    </div>
                    {/*<div className="d-flex align-items-center justify-content-center">
                      <i className="fa fa-square me-2 black-icon"></i>
                      <span className="fs-10px fw-semibold black-text">
                        SOLICITUDES
                      </span>
                    </div>*/}
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-sm-6 col-xl-6">
              <Card className="h-100">
                <CardHeader className="d-flex">SOLICITUDES </CardHeader>
                <CardBody>
                  <div className="d-flex align-items-center h3 mb-0">
                    <div className="black-text">{mailData.length}</div>
                  </div>
                  <hr className="black-text" />
                  <div className="row mb-2">
                    <div className="col-6 black-text">Formalizado</div>
                    <div className="col-3 text-center black-text">
                      {mailData.reduce(
                        (acc, curr) =>
                          acc + (curr.sub_status === "Formalizado" ? 1 : 0),
                        0
                      )}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-6 black-text">Rechazado</div>
                    <div className="col-3 text-center black-text">
                      {mailData.reduce(
                        (acc, curr) =>
                          acc + (curr.sub_status === "Rechazado" ? 1 : 0),
                        0
                      )}
                    </div>
                  </div>
                  <hr className="black-text" />
                  <div className="fs-10px fw-semibold mb-3 black-text">
                    SOLICITUDES A LO LARGO DEL TIEMPO
                  </div>
                  <div className="chart mb-2" style={{ height: "190px" }}>
                    <div id="chart-2"></div>
                  </div>
                  <div className="d-flex align-items-center justify-content-center">
                    <i className="fa fa-square text-gray-300 me-2"></i>
                    <span className="fs-10px fw-semibold me-4 black-text">
                      Formalizado
                    </span>
                    <i className="fa fa-square me-2"></i>
                    <span className="fs-10px fw-semibold black-text">
                      Rechazado
                    </span>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-sm-6 col-xl-6">
              <Card className="h-100">
                <CardHeader className="d-flex">SOLICITUDES</CardHeader>
                <CardBody>
                  <div>
                    <div className="fs-10px fw-semibold mb-3 black-text">
                      CLIENTES
                    </div>
                    <div className="chart mb-2" style={{ height: "190px" }}>
                      <div id="chart-3"></div>
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-sm-12 col-xl-12">
              <Card>
                <CardHeader className="d-flex">TOTAL DE SOLICITUDES</CardHeader>
                <CardBody>
                  <div className="row mb-2">
                    <div className="col-6 black-text">TOTAL DE SOLICITUDES</div>
                    <div className="col-3 text-center black-text">
                      {mailData.length} {/* Total de solicitudes */}
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6 black-text">Autorizados</div>
                    <div className="col-3 text-center black-text">
                      {
                        mailData.filter((item) => item.status === "Autorizado").length
                      }{" "}
                      {/* Aprobados */}
                    </div>
                  </div>
                  <div className="row mb-2">
                    <div className="col-6 black-text">Rechazados</div>
                    <div className="col-3 text-center black-text">
                      {
                         mailData.filter((item) => item.status === "Rechazado").length
                      }{" "}
                      {/* Rechazados */}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </div>

            <div className="col-sm-12 col-xl-12">
              <Card>
                <CardHeader className="d-flex">
                  Solicitudes de Crédito Automotriz
                </CardHeader>
                <CardBody>
                  <div className="chart mb-2" style={{ height: "350px" }}>
                    <ApexCharts
                      options={options}
                      series={funnelData.series}
                      type="bar"
                      height={350}
                    />
                  </div>
                </CardBody>
              </Card>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Analytics;
